import { useCallback, useEffect, useRef, useState } from "react";
import { Box, Chip, LinearProgress, Stack, Typography } from "@mui/material";
import { useLocation, useParams } from "react-router-dom";
import { axiosPost } from "api";
import useCrud from "hooks/useCrud";
import PageLayout from "components/layout/PageLayout";
import MobileFooterLayout from "components/layout/MobileFooterLayout";
import { typographyStyles } from "constants/styles";
import { STATUS_COLOR, WORK_ORDER_STATUS_LABEL } from "helpers/work_orders";
import FractionationBundleCard from "components/WorkOrders/Fractionation/FractionationBundleCard";
import FractionationBundleEditor from "components/WorkOrders/Fractionation/FractionationBundleEditor";
import FractionationScanner from "components/WorkOrders/Fractionation/FractionationScanner";
import { useToast } from "hooks/useToast";
import { useSession } from "providers/SessionProvider";
import { WORK_ORDER_STATUS } from "constants/index";
import {
  WORK_ORDERS_URL,
  getWorkOrderBundleUrl,
  getWorkOrderBundleClaimUrl,
  getWorkOrderBundleReleaseUrl,
} from "constants/urls";
import type {
  IMovementWorkOrderBundleUpdate,
  IMovementWorkOrderBundleDetail,
  IMovementWorkOrderDetail,
  IMovementWorkOrderBundleResult,
} from "types";

export default function FractionationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigationState: unknown = location.state;
  const scannedLot =
    navigationState &&
    typeof navigationState === "object" &&
    "scannedLot" in navigationState &&
    typeof navigationState.scannedLot === "string"
      ? navigationState.scannedLot
      : null;
  const handledScanKey = useRef<string | null>(null);
  const { showSnackBar } = useToast();
  const { userData } = useSession();
  const [order, setOrder] = useState<IMovementWorkOrderDetail | null>(null);
  const [editor, setEditor] = useState<IMovementWorkOrderBundleDetail | null>(
    null,
  );
  const [editorOpen, setEditorOpen] = useState(false);
  const { fetchData } = useCrud<IMovementWorkOrderDetail>(WORK_ORDERS_URL, {
    disableAutoFetch: true,
  });
  const { updateData: saveBundle } = useCrud<IMovementWorkOrderBundleUpdate>(
    getWorkOrderBundleUrl(id ?? "", ""),
    { disableAutoFetch: true },
  );
  const { createData: releaseBundle } = useCrud<Record<string, never>>(
    id && editor ? getWorkOrderBundleReleaseUrl(id, editor.id) : "",
    { disableAutoFetch: true },
  );
  // useCrud recreates fetchData on every render; keep load stable to avoid extra GETs.
  const fetchOrder = useRef(fetchData);
  fetchOrder.current = fetchData;
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [stale, setStale] = useState(false);
  const [loadedLocationKey, setLoadedLocationKey] = useState<string | null>(null);
  const currentId = useRef(id);
  const currentEditor = useRef(editor);
  const pending = useRef(false);
  const loadSequence = useRef(0);
  const mounted = useRef(true);
  currentId.current = id;
  currentEditor.current = editorOpen ? editor : null;

  const load = useCallback(async () => {
    if (!id) return;
    const sequence = ++loadSequence.current;
    const response = await fetchOrder.current(id, true);
    if (
      !mounted.current ||
      currentId.current !== id ||
      sequence !== loadSequence.current ||
      currentEditor.current
    )
      return;
    if (response.status === 200) {
      const loadedOrder = response.data as IMovementWorkOrderDetail;
      setOrder(loadedOrder);
      setLoadedLocationKey(location.key);
      setStale(false);
      setLoading(false);
      return loadedOrder;
    } else {
      setStale(true);
      showSnackBar(
        "No se pudo actualizar la orden. Volvé a ingresar para ver su estado actual.",
        "error",
      );
    }
    setLoading(false);
    return null;
  }, [id, location.key, showSnackBar]);

  useEffect(() => {
    mounted.current = true;
    currentEditor.current = null;
    pending.current = false;
    setEditor(null);
    setEditorOpen(false);
    setOrder(null);
    setLoadedLocationKey(null);
    setBusy(false);
    setStale(false);
    setLoading(true);
    void load();
    return () => {
      mounted.current = false;
    };
  }, [load]);

  const openBundle = async (bundle: IMovementWorkOrderBundleDetail) => {
    if (!id || pending.current || currentEditor.current || stale) return;
    if (bundle.completed) {
      ++loadSequence.current;
      currentEditor.current = bundle;
      setEditor(bundle);
      setEditorOpen(true);
      return;
    }
    pending.current = true;
    ++loadSequence.current;
    setBusy(true);
    const response = await axiosPost(
      getWorkOrderBundleClaimUrl(id, bundle.id),
      {},
      true,
    );
    if (!mounted.current || currentId.current !== id) return;
    if (response.status === 200) {
      const result = response.data as IMovementWorkOrderBundleResult;
      currentEditor.current = result.bundle;
      setEditor(result.bundle);
      setEditorOpen(true);
    } else {
      showSnackBar(
        response.data?.message ?? "No se pudo tomar el fardo.",
        "error",
      );
      if (response.status === 409) {
        setStale(true);
        await load();
        if (!mounted.current || currentId.current !== id) return;
      }
    }
    pending.current = false;
    setBusy(false);
  };

  useEffect(() => {
    if (
      !order ||
      String(order.id) !== id ||
      loadedLocationKey !== location.key ||
      !scannedLot ||
      handledScanKey.current === location.key ||
      stale
    ) return;
    handledScanKey.current = location.key;
    const bundle = order.bundles.find((item) => item.lote_externo === scannedLot);
    if (bundle) void openBundle(bundle);
    else
      showSnackBar(
        `No hay un fardo con lote externo ${scannedLot} en esta orden.`,
        "error",
      );
    // Solo se abre al llegar con un escaneo; openBundle cambia con cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order, stale, id, location.key, scannedLot, loadedLocationKey]);

  const finishEditing = async (values?: IMovementWorkOrderBundleUpdate) => {
    const selected = currentEditor.current;
    if (!id || !selected || pending.current || (selected.completed && values))
      return;
    pending.current = true;
    setBusy(true);
    if (selected.completed) {
      currentEditor.current = null;
      setEditorOpen(false);
      setStale(true);
      await load();
      if (!mounted.current || currentId.current !== id) return;
      pending.current = false;
      setBusy(false);
      return;
    }
    const response = values
      ? await saveBundle(selected.id, values, true)
      : await releaseBundle({}, true);
    if (!mounted.current || currentId.current !== id) return;
    if (response.status === 200 || response.status === 409) {
      // A conflict can be a retry of a committed operation whose response was lost.
      // Close the editor and reload the OT once, just as after a successful save/cancel.
      currentEditor.current = null;
      setEditorOpen(false);
      setStale(true);
      const updatedOrder = await load();
      if (!mounted.current || currentId.current !== id) return;
      const updatedBundle = updatedOrder?.bundles.find(
        (bundle) => bundle.id === selected.id,
      );
      if (
        updatedOrder &&
        (response.status === 200 ||
          (values &&
            updatedBundle?.completed &&
            updatedBundle.operator_id === userData?.id))
      ) {
        showSnackBar(
          values ? "Fardo completado. Stock actualizado." : "Fardo liberado.",
          "success",
        );
      } else if (updatedOrder) {
        showSnackBar(
          response.data?.message ??
            "El estado del fardo cambió. Revisá la orden actualizada.",
          "error",
        );
      }
    } else
      showSnackBar(
        response.data?.message ??
          "No se pudo completar la operación. Podés reintentar.",
        "error",
      );
    if (!mounted.current || currentId.current !== id) return;
    pending.current = false;
    setBusy(false);
  };

  if (loading) {
    return (
      <PageLayout pageTitle="Fraccionado" pageDesc="Orden de trabajo">
        <Typography role="status">Cargando orden…</Typography>
      </PageLayout>
    );
  }

  if (!order) {
    return (
      <PageLayout pageTitle="Fraccionado" pageDesc="Orden de trabajo">
        <Typography>No se encontró la orden.</Typography>
      </PageLayout>
    );
  }

  const isFinished =
    order.status === WORK_ORDER_STATUS.CONFIRMED ||
    order.status === WORK_ORDER_STATUS.FINISHED;
  return (
    <PageLayout
      pageTitle={
        <Stack spacing={0.5}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={typographyStyles.monospace}
          >
            Remito: {order.movement.client_document || "Sin documento"}
          </Typography>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            flexWrap="wrap"
          >
            <Typography variant="h5" fontWeight="bold">
              Orden #{order.id}
            </Typography>
            <Chip
              size="small"
              label={WORK_ORDER_STATUS_LABEL[order.status]}
              color={STATUS_COLOR[order.status]}
            />
          </Stack>
        </Stack>
      }
      pageDesc=""
    >
      <Stack
        spacing={2}
        sx={{ maxWidth: 680, mx: "auto", pb: { xs: 16, md: 4 } }}
      >
        {order.bundles.map((bundle) => (
          <Box key={bundle.id}>
            <FractionationBundleCard
              bundle={bundle}
              editable={!isFinished}
              operatorId={userData?.id}
              busy={busy || stale}
              onEdit={() => void openBundle(bundle)}
            />
          </Box>
        ))}
      </Stack>
      <FractionationBundleEditor
        open={editorOpen}
        key={editor?.id}
        bundle={editor}
        busy={busy}
        onCancel={() => void finishEditing()}
        onSave={(values) => void finishEditing(values)}
      />
      <MobileFooterLayout>
        <Stack
          role="region"
          aria-label="Progreso de fraccionado"
          spacing={1}
          sx={{ maxWidth: 680, mx: "auto" }}
        >
          <Typography
            variant="body2"
            fontWeight={700}
            color={`${STATUS_COLOR[order.status]}.main`}
          >
            {order.completed_bundles} de {order.total_bundles} fardos
            completados
          </Typography>
          <LinearProgress
            color={STATUS_COLOR[order.status]}
            variant="determinate"
            aria-label="Fardos completados"
            value={
              order.total_bundles
                ? (100 * order.completed_bundles) / order.total_bundles
                : 0
            }
            sx={{ height: 7, borderRadius: 1 }}
          />
        </Stack>
      </MobileFooterLayout>
      <FractionationScanner
        aboveMobileFooter
        disabled={busy || stale || editorOpen}
      />
    </PageLayout>
  );
}
